"use client";

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { upload } from '@vercel/blob/client';
import type { PutBlobResult } from '@vercel/blob';
import {
  Download,
  Info,
  Loader2,
  AlertTriangle,
  ExternalLink,
  Sparkles,
  ListChecks,
  GaugeCircle,
  User,
  Clock,
  Eye,
  ThumbsUp,
  Braces,
  Activity,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { Badge } from '@/app/components/ui/badge';
import { Textarea } from '@/app/components/ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/app/components/ui/accordion';
import { Progress } from '@/app/components/ui/progress';
import { buildFormatSelector } from '@/lib/format';

type ProgressEvent = {
  downloadedBytes: number;
  totalBytes?: number;
  speedBps?: number;
  etaSec?: number;
  id?: string;
  title?: string;
};

// Fallback parser for templated yt-dlp progress lines emitted to stdout/stderr
function parseProgressFromTemplateLine(line: string): ProgressEvent | null {
  let payload = line.trim();
  if (!payload) return null;
  if (payload.startsWith('download:')) payload = payload.slice('download:'.length).trim();
  const parts = payload.split('|');
  if (parts.length < 9) return null;
  const d = parts[0];
  const t = parts[1];
  const te = parts[2];
  const s = parts[3];
  const e = parts[4];
  const fs = parts[5];
  const fsa = parts[6];
  const id = parts[7];
  const title = parts.slice(8).join('|');
  const toNum = (v?: string) => {
    if (v === undefined || v === null) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const downloadedBytes = toNum(d);
  if (downloadedBytes === undefined) return null;
  const candidates = [toNum(t), toNum(te), toNum(fs), toNum(fsa)];
  const totalBytes = candidates.find((x) => typeof x === 'number' && Number.isFinite(x));
  return { downloadedBytes, totalBytes, speedBps: toNum(s), etaSec: toNum(e), id, title };
}

// Regex-based parser for default yt-dlp progress lines like:
// [download]  45.2% of 10.5MiB at  2.1MiB/s ETA 00:03
function parseDefaultProgressClient(line: string): ProgressEvent | null {
  const m = line.match(/\[download\]\s+(\d+\.?\d*)% of ([\d.]+[KMGT]?i?B) at\s+([\d.]+[KMGT]?i?B)\/s ETA (\d+):(\d+)/i);
  if (!m) return null;
  const percent = parseFloat(m[1]);
  const totalStr = m[2];
  const speedStr = m[3];
  const mm = parseInt(m[4]);
  const ss = parseInt(m[5]);
  const parseBytes = (str: string): number | undefined => {
    const match = str.match(/^([\d.]+)([KMGT]?i?B)$/i);
    if (!match) return undefined;
    let [, num, unit] = match;
    let multiplier = 1;
    const u = unit.toLowerCase();
    if (u.includes('k')) multiplier = 1024;
    else if (u.includes('m')) multiplier = 1024 * 1024;
    else if (u.includes('g')) multiplier = 1024 * 1024 * 1024;
    return parseFloat(num) * multiplier;
  };
  const totalBytes = parseBytes(totalStr);
  const speedBps = parseBytes(speedStr);
  const downloadedBytes = totalBytes ? (totalBytes * percent) / 100 : undefined;
  return downloadedBytes === undefined
    ? null
    : { downloadedBytes, totalBytes: totalBytes, speedBps, etaSec: mm * 60 + ss };
}

function calcPercent(p?: ProgressEvent): number | undefined {
  if (!p?.totalBytes || p.totalBytes <= 0) return undefined;
  return Math.min(100, Math.round((p.downloadedBytes / p.totalBytes) * 100));
}

// Helpers to tidy log output
function baseNameFromQuotedPath(line: string): string | null {
  const m = line.match(/"([^"\\]+(?:\\.[^"\\]+)*)"/);
  const raw = m ? m[1] : null;
  if (!raw) return null;
  const parts = raw.split(/[\\/]/);
  return parts[parts.length - 1] || raw;
}

function summarizeYtDlpLog(line: string): string | null {
  const l = line.trim();
  if (!l) return null;
  // Ignore very noisy progress-like or metadata lines
  if ((l.match(/\|/g) || []).length >= 6) return null; // likely our template line
  if (/^\[download\]\s+\d+/.test(l)) return null; // default progress

  // Friendly summaries
  let m: RegExpMatchArray | null;
  if ((m = l.match(/^\[Merger\]\s+Merging formats into\s+"(.+)"/i))) {
    const name = baseNameFromQuotedPath(`"${m[1]}"`) || m[1];
    return `Merging formats → ${name}`;
  }
  if ((m = l.match(/^\[download\]\s+Destination:\s+(.+)/i))) {
    const name = baseNameFromQuotedPath(m[1]) || (m[1].split(/[\\/]/).pop() || m[1]);
    return `Saving to → ${name}`;
  }
  if ((m = l.match(/^Deleting original file (.+) \(pass -k to keep\)/i))) {
    const name = baseNameFromQuotedPath(m[1]) || (m[1].split(/[\\/]/).pop() || m[1]);
    return `Cleaning up → ${name}`;
  }
  if ((m = l.match(/^\[download\]\s+(.+?) has already been downloaded/i))) {
    const name = baseNameFromQuotedPath(m[1]) || (m[1].split(/[\\/]/).pop() || m[1]);
    return `Already downloaded → ${name}`;
  }
  if (/^\[ExtractAudio\]/i.test(l)) return l.replace(/^\[ExtractAudio\]\s*/i, 'Extracting audio → ');
  if (/^\[Fixup/i.test(l)) return l.replace(/^\[(Fixup[^\]]+)\]\s*/i, (_, tag) => `${tag} → `);
  if (/^\[generic\]/i.test(l)) return null; // often noisy fetch logs
  if (/^\[redirect\]/i.test(l)) return null;
  if (/^\[info\]/i.test(l)) return l.replace(/^\[info\]\s*/i, 'Info → ');
  if (/^\[warning\]/i.test(l)) return l.replace(/^\[warning\]\s*/i, 'Warning → ');
  if (/^\[error\]/i.test(l)) return l.replace(/^\[error\]\s*/i, 'Error → ');
  return l; // default: pass through trimmed line
}

function formatTime(ts: number): string {
  const d = new Date(ts);
  try {
    return d.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch {
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${hh}:${mm}:${ss}`;
  }
}

type ParsedVideoInfo = {
  title: string;
  duration?: number;
  thumbnail?: string;
  uploader?: string;
  viewCount?: number;
  likeCount?: number;
  bestFormat?: {
    formatId?: string;
    format?: string;
    ext?: string;
    height?: number;
    filesize?: number;
    filesizeApprox?: number;
    formatNote?: string;
  };
  raw: any;
};

type ServerDownloadResult = {
  tmpToken: string;
  tmpUrl: string;
  fileName: string;
  size: number;
  format: string;
  contentType: string;
  expiresAt: number;
};

type DownloadResult = {
  url: string;
  pathname: string;
  size: number;
  format: string;
  contentType: string;
};

type PreflightResult = {
  recommendedFormat: string;
  estimate: {
    id?: string;
    title?: string;
    filesize?: string;
    duration?: string;
    ext?: string;
  } | null;
};

type LoadingAction = 'info' | 'preflight' | 'download' | 'sse' | 'upload' | null;

type FormState = {
  url: string;
  baseName: string;
  rateLimit: string;
  headers: string;
  cookiesText: string;
  proxy: string;
};

const DEFAULT_FORM: FormState = {
  url: '',
  baseName: '',
  rateLimit: '',
  headers: '',
  cookiesText: '',
  proxy: '',
};

interface YouTubeDownloaderProps {
  className?: string;
}

export function YouTubeDownloader({ className }: YouTubeDownloaderProps) {
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [loadingAction, setLoadingAction] = useState<LoadingAction>(null);
  const [error, setError] = useState<string>('');
  const [info, setInfo] = useState<ParsedVideoInfo | null>(null);
  const [preflight, setPreflight] = useState<PreflightResult | null>(null);
  const [downloadResult, setDownloadResult] = useState<DownloadResult | null>(null);
  const [availableFormats, setAvailableFormats] = useState<Array<{
    id: string;
    ext: string;
    resolution?: string;
    fps?: number;
    filesize?: number;
    filesize_approx?: number;
    tbr?: number;
    vbr?: number;
    abr?: number;
    asr?: number;
    protocol: string;
    vcodec?: string;
    acodec?: string;
    format_note?: string;
    quality?: number;
    width?: number;
    height?: number;
    container?: string;
    audio_channels?: number;
  }> | null>(null);
  const [progress, setProgress] = useState<{ status: 'idle' | 'downloading' | 'complete' | 'error'; data?: ProgressEvent; message?: string }>({
    status: 'idle',
  });
  const [selectedFormatId, setSelectedFormatId] = useState<string>('');
  const [sseController, setSseController] = useState<AbortController | null>(null);
  // Keep the latest progress in a ref to avoid stale state inside callbacks
  const latestProgressRef = useRef<ProgressEvent | undefined>(undefined);
  // Track whether we received a 'done' event before the stream closed
  const sseCompletedRef = useRef<boolean>(false);
  // Live console logs from SSE 'log' events
  const [logs, setLogs] = useState<Array<{ stream: 'stdout' | 'stderr'; line: string; ts: number }>>([]);
  const [rawLogs, setRawLogs] = useState<Array<{ stream: 'stdout' | 'stderr'; line: string; ts: number }>>([]);
  const [logView, setLogView] = useState<'summary' | 'raw'>('summary');
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  const [showTimestamps, setShowTimestamps] = useState<boolean>(true);
  const consoleRef = useRef<HTMLDivElement | null>(null);

  const isLoading = loadingAction !== null;

  const progressPercent = useMemo(() => {
    if (!progress.data?.totalBytes || progress.data.totalBytes === 0) return undefined;
    return Math.min(100, Math.round((progress.data.downloadedBytes / progress.data.totalBytes) * 100));
  }, [progress]);

  const downloadedBytes = progress.data?.downloadedBytes;
  const totalBytes = progress.data?.totalBytes;
  const speedBps = progress.data?.speedBps;
  const etaSec = progress.data?.etaSec;
  const title = progress.data?.title;

  const currentFormatSelector = useMemo(() => {
    return buildFormatSelector({});
  }, []);

  const handleFieldChange = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const selectedFormatString = useMemo(() => {
    if (!selectedFormatId) return undefined; // auto
    const fmt = availableFormats?.find(f => String(f.id) === String(selectedFormatId));
    if (!fmt) return undefined;
    const hasVideo = fmt.vcodec && fmt.vcodec !== 'none';
    const hasAudio = fmt.acodec && fmt.acodec !== 'none';
    if (hasVideo && hasAudio) return String(fmt.id);
    if (hasVideo && !hasAudio) return `${fmt.id}+bestaudio/best`;
    if (!hasVideo && hasAudio) return String(fmt.id);
    return String(fmt.id);
  }, [selectedFormatId, availableFormats]);

  const parseHeadersInput = (input: string): Record<string, string> | undefined => {
    const lines = input
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (!lines.length) return undefined;
    const headers: Record<string, string> = {};
    for (const line of lines) {
      const idx = line.indexOf(':');
      if (idx === -1) {
        throw new Error('Headers must be specified as "Name: Value"');
      }
      const name = line.slice(0, idx).trim();
      const value = line.slice(idx + 1).trim();
      if (!name || !value) {
        throw new Error('Headers must include both a name and a value');
      }
      headers[name] = value;
    }
    return headers;
  };

  const buildBasePayload = () => {
    if (!form.url.trim()) {
      throw new Error('Please enter a YouTube URL');
    }

    const headers = parseHeadersInput(form.headers);
    const rateLimit = form.rateLimit.trim()
      ? Number(form.rateLimit.trim())
      : undefined;

    if (rateLimit !== undefined) {
      if (!Number.isFinite(rateLimit) || rateLimit <= 0) {
        throw new Error('Rate limit must be a positive number (kbps)');
      }
    }

    return {
      url: form.url.trim(),
      baseName: form.baseName.trim() || undefined,
      rateLimitKbps: rateLimit,
      headers,
      cookiesText: form.cookiesText.trim() || undefined,
      proxy: form.proxy.trim() || undefined,
    };
  };

  const fetchJson = async <T,>(
    path: string,
    body: Record<string, unknown>,
  ): Promise<T> => {
    const response = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });

    let payload: any = null;
    try {
      payload = await response.json();
    } catch (error) {
      // ignore JSON parse errors for non-JSON responses
    }

    if (!response.ok) {
      throw new Error(payload?.error ?? 'Request failed');
    }
    return payload as T;
  };

  const handleGetInfo = async () => {
    try {
      setError('');
      setLoadingAction('info');
      setDownloadResult(null);
      setProgress({ status: 'idle' });
      setAvailableFormats(null);
      setSelectedFormatId('');

      const basePayload = buildBasePayload();
      const payload = {
        url: basePayload.url,
        headers: basePayload.headers,
        cookiesText: basePayload.cookiesText,
      };

      // Fetch both info and formats in parallel
      const [infoResult, formatsResult] = await Promise.all([
        fetchJson<any>('/api/yt/info', payload),
        fetchJson<{ formats: any[] }>('/api/yt/formats', payload).catch(() => ({ formats: [] }))
      ]);

      const parsed = parseVideoInfo(infoResult);
      setInfo(parsed);
      setAvailableFormats(formatsResult.formats);
      toast.success('Video metadata and formats retrieved');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch metadata';
      setError(message);
      toast.error(message);
    } finally {
      setLoadingAction(null);
    }
  };

  const handlePreflight = async () => {
    try {
      setError('');
      setPreflight(null);
      setLoadingAction('preflight');

      const basePayload = buildBasePayload();
      const payload = {
        url: basePayload.url,
        headers: basePayload.headers,
        cookiesText: basePayload.cookiesText,
      };

      const result = await fetchJson<PreflightResult>('/api/yt/preflight', payload);
      setPreflight(result);
      toast.success('Preflight complete');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Preflight failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDownload = async () => {
    try {
      setError('');
      setDownloadResult(null);
      setLoadingAction('download');

      const payload = buildBasePayload();
      const payloadWithFormat = {
        ...payload,
        format: selectedFormatString,
      } as any;
      const server = await fetchJson<ServerDownloadResult>('/api/yt/download', payloadWithFormat);
      const absoluteUrl = new URL(server.tmpUrl, window.location.origin).toString();
      const mapped: DownloadResult = {
        url: absoluteUrl,
        pathname: server.tmpUrl,
        size: server.size,
        format: server.format,
        contentType: server.contentType,
      };
      setDownloadResult(mapped);
      toast.success('Download ready');
      window.open(mapped.url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Download failed';
      setError(message);
      toast.error(message);
    } finally {
      setLoadingAction(null);
    }
  };

  const handleDownloadWithProgress = async () => {
    try {
      setError('');
      setDownloadResult(null);
      setProgress({ status: 'downloading' });
      setLoadingAction('sse');
      latestProgressRef.current = undefined;
      sseCompletedRef.current = false;
      setLogs([]);
      setRawLogs([]);

      const payload = buildBasePayload();
      const payloadWithFormat = {
        ...payload,
        format: selectedFormatString,
      } as any;
      const controller = new AbortController();
      setSseController(controller);
      const response = await fetch('/api/yt/download/sse', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payloadWithFormat),
        signal: controller.signal,
      });

      if (!response.ok || !response.body) {
        const detail = await response.text();
        throw new Error(detail || 'Failed to start download');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const text = decoder.decode(value, { stream: true });
        // Normalize CRLF to LF so boundary detection works regardless of line endings
        buffer += text.replace(/\r\n/g, '\n');
        // Prevent unbounded memory usage if boundaries are never found
        if (buffer.length > 1_000_000) buffer = buffer.slice(-1_000_000);
        let boundary = buffer.indexOf('\n\n');
        while (boundary !== -1) {
          const chunk = buffer.slice(0, boundary);
          buffer = buffer.slice(boundary + 2);
          handleSseChunk(chunk);
          boundary = buffer.indexOf('\n\n');
        }
      }
      // If the stream ended without a 'done' event, surface an error
      if (!sseCompletedRef.current) {
        setProgress((prev) => prev.status === 'complete' ? prev : { status: 'error', message: 'Connection closed before completion' });
        setError((prev) => prev || 'Connection closed before completion');
        toast.error('Connection closed before completion');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Download failed';
      setError(message);
      setProgress({ status: 'error', message });
      toast.error(message);
    } finally {
      setSseController(null);
      setLoadingAction(null);
    }
  };

  const handleCancelDownload = () => {
    try {
      sseController?.abort();
      setProgress({ status: 'idle' });
      toast('Download cancelled');
    } catch {}
  };

  const handleSseChunk = (chunk: string) => {
    if (!chunk.trim()) return;
    console.log('Received SSE chunk:', chunk); // Debug: log received chunks
    const lines = chunk.split('\n');
    let event: string | null = null;
    let data: string | null = null;
    for (const line of lines) {
      if (line.startsWith('event:')) {
        event = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        data = line.slice('data:'.length).trim();
      }
    }
    if (!event || !data) return;

    try {
      const parsed = JSON.parse(data);
      if (event === 'progress') {
        latestProgressRef.current = parsed as ProgressEvent;
        setProgress({ status: 'downloading', data: latestProgressRef.current });
      } else if (event === 'log') {
        const item = parsed as { stream: 'stdout' | 'stderr'; line: string };
        // Always record raw logs (trim + de-dup, cap)
        const rawTrim = item.line.trim();
        if (rawTrim) {
          setRawLogs((prevRaw) => {
            const last = prevRaw[prevRaw.length - 1];
            if (last && last.stream === item.stream && last.line === rawTrim) return prevRaw;
            const next = [...prevRaw, { stream: item.stream, line: rawTrim, ts: Date.now() }];
            return next.length > 1000 ? next.slice(-1000) : next;
          });
        }
        // Try to parse progress from templated or default lines
        const prev = latestProgressRef.current;
        const maybe = parseProgressFromTemplateLine(item.line) || parseDefaultProgressClient(item.line);
        if (maybe) {
          const prevPct = calcPercent(prev) ?? 0;
          const nextPct = calcPercent(maybe);
          latestProgressRef.current = maybe;
          setProgress({ status: 'downloading', data: latestProgressRef.current });
          // Optionally append a compact progress summary only on meaningful jumps
          if (nextPct !== undefined && (nextPct === 100 || nextPct - prevPct >= 5)) {
            const summary = `Progress ${nextPct}% of ${maybe.totalBytes ? formatBytes(maybe.totalBytes) : 'unknown'}${maybe.speedBps ? ` at ${formatSpeed(maybe.speedBps)}` : ''}${maybe.etaSec ? ` · ${formatEta(maybe.etaSec)}` : ''}`;
            setLogs((prevLogs) => {
              const last = prevLogs[prevLogs.length - 1];
              if (last && last.line === summary) return prevLogs; // de-dupe
              const next = [...prevLogs, { stream: 'stdout' as const, line: summary, ts: Date.now() }];
              return next.length > 500 ? next.slice(-500) : next;
            });
          }
        } else {
          // Not a progress line: summarize and de-duplicate
          const summarized = summarizeYtDlpLog(item.line);
          if (!summarized) return; // filtered out
          setLogs((prevLogs) => {
            const last = prevLogs[prevLogs.length - 1];
            if (last && last.line === summarized) return prevLogs; // skip duplicates
            const next = [...prevLogs, { stream: item.stream, line: summarized, ts: Date.now() }];
            return next.length > 500 ? next.slice(-500) : next;
          });
        }
      } else if (event === 'done') {
        sseCompletedRef.current = true;
        const server = parsed as ServerDownloadResult;
        // Ensure the bar shows 100% at completion even if we never parsed progress
        const completed: ProgressEvent = latestProgressRef.current ?? { downloadedBytes: server.size, totalBytes: server.size };
        if (!completed.totalBytes && server.size) completed.totalBytes = server.size;
        if (!completed.downloadedBytes && server.size) completed.downloadedBytes = server.size;
        setProgress({ status: 'complete', data: completed });
        const absoluteUrl = new URL(server.tmpUrl, window.location.origin).toString();
        const mapped: DownloadResult = {
          url: absoluteUrl,
          pathname: server.tmpUrl,
          size: server.size,
          format: server.format,
          contentType: server.contentType,
        };
        setDownloadResult(mapped);
        toast.success('Download complete');
        window.open(mapped.url, '_blank', 'noopener,noreferrer');
      } else if (event === 'error') {
        const message = typeof parsed === 'string' ? parsed : parsed?.message ?? 'Download failed';
        const reason = typeof parsed === 'object' && parsed !== null ? (parsed as any).reason : undefined;
        setProgress({ status: 'error', message });
        setError(message);
        if (reason === 'BLOB_SIZE_LIMIT') {
          toast.error('Upload failed: server limit is 4.5 MB for server uploads. Try Client upload or smaller format.');
        } else {
          toast.error(message);
        }
      }
    } catch (err) {
      console.error('Failed to parse SSE chunk', err);
    }
  };

  // Auto-scroll console when logs change
  useEffect(() => {
    if (!autoScroll) return;
    const el = consoleRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [autoScroll, logView, logs, rawLogs]);

  const resetAll = () => {
    setForm(DEFAULT_FORM);
    setInfo(null);
    setPreflight(null);
    setDownloadResult(null);
    setAvailableFormats(null);
    setProgress({ status: 'idle' });
    setError('');
  };

  return (
    <div className={className}>
      <Card className="w-full">
        <CardHeader>
          <div className="flex items-center justify-between gap-2">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                <Download className="h-5 w-5" />
                YouTube Downloader
              </CardTitle>
              <CardDescription>
                Download YouTube videos with intelligent format selection and robust fallbacks. Supports MP4, WebM, and MKV containers.
              </CardDescription>
            </div>
            <Button variant="ghost" onClick={resetAll} disabled={isLoading}>
              Reset
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-8">
          <section className="grid gap-6 md:grid-cols-2">
            <div className="space-y-3">
              <Label htmlFor="yt-url">YouTube URL</Label>
              <Input
                id="yt-url"
                type="url"
                placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                value={form.url}
                onChange={(event) => handleFieldChange('url', event.target.value)}
                disabled={isLoading}
              />
            </div>
            <div className="space-y-3">
              <Label htmlFor="base-name">Base file name</Label>
              <Input
                id="base-name"
                placeholder="Defaults to video title"
                value={form.baseName}
                onChange={(event) => handleFieldChange('baseName', event.target.value)}
                disabled={isLoading}
              />
            </div>
          </section>

          {/* Simple Format Picker */}
          <div className="grid gap-2">
            <Label htmlFor="format-select">Preferred format</Label>
            <Select
              value={selectedFormatId || undefined}
              onValueChange={(value) => setSelectedFormatId(value)}
            >
              <SelectTrigger id="format-select">
                <SelectValue placeholder="Auto (bestvideo+bestaudio/best)" />
              </SelectTrigger>
              <SelectContent>
                {availableFormats?.slice(0, 30).map((f) => (
                  <SelectItem key={String(f.id)} value={String(f.id)}>
                    {`${f.id} • ${f.ext.toUpperCase()} ${f.resolution ?? ''} ${f.fps ? `${f.fps}fps` : ''}`.trim()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">Auto merges best video and audio when needed. Pick a specific itag to force a format.</p>
          </div>

          <Accordion type="multiple" className="w-full">
            <AccordionItem value="network">
              <AccordionTrigger className="text-left">
                <div className="flex items-center gap-2">
                  <GaugeCircle className="h-4 w-4" />
                  Networking & rate limits
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid gap-4 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="rate-limit">Rate limit (kbps)</Label>
                    <Input
                      id="rate-limit"
                      type="number"
                      min="128"
                      step="64"
                      value={form.rateLimit}
                      onChange={(event) => handleFieldChange('rateLimit', event.target.value)}
                      disabled={isLoading}
                      placeholder="Optional"
                    />
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="proxy">Proxy URL</Label>
                    <Input
                      id="proxy"
                      type="url"
                      placeholder="http://user:pass@host:port"
                      value={form.proxy}
                      onChange={(event) => handleFieldChange('proxy', event.target.value)}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="headers">
              <AccordionTrigger className="text-left">
                <div className="flex items-center gap-2">
                  <Braces className="h-4 w-4" />
                  Custom headers & cookies
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="custom-headers">HTTP headers</Label>
                    <Textarea
                      id="custom-headers"
                      placeholder={`User-Agent: ...\nAccept-Language: en-US,en;q=0.9`}
                      value={form.headers}
                      onChange={(event) => handleFieldChange('headers', event.target.value)}
                      disabled={isLoading}
                      className="min-h-32"
                    />
                    <p className="text-xs text-muted-foreground">One header per line. `Name: value` format.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cookies">Netscape cookies.txt</Label>
                    <Textarea
                      id="cookies"
                      placeholder="# Netscape HTTP Cookie File\n..."
                      value={form.cookiesText}
                      onChange={(event) => handleFieldChange('cookiesText', event.target.value)}
                      disabled={isLoading}
                      className="min-h-32"
                    />
                    <p className="text-xs text-muted-foreground">Paste the raw cookies file (including header) for age-restricted content.</p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <section className="flex flex-wrap gap-3">
            <Button
              onClick={handleGetInfo}
              disabled={isLoading}
              variant="outline"
            >
              {loadingAction === 'info' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Info className="mr-2 h-4 w-4" />}
              Metadata
            </Button>
            <Button onClick={handleDownload} disabled={isLoading}>
              {loadingAction === 'download' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              Download to Blob
            </Button>
            <Button onClick={handleDownloadWithProgress} disabled={isLoading} variant="secondary">
              {loadingAction === 'sse' ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Activity className="mr-2 h-4 w-4" />
              )}
              Download with progress
            </Button>
          </section>

          {progress.status !== 'idle' && (
            <Card className="border-primary/40 bg-primary/5">
              <CardHeader className="space-y-1 sticky top-0 z-10 bg-background/80 backdrop-blur supports-backdrop-filter:bg-background/60">
                <CardTitle className="flex items-center gap-2 text-base">
                  <GaugeCircle className="h-4 w-4 text-primary" />
                  Live progress
                  {title && (
                    <span className="text-sm font-normal text-muted-foreground truncate max-w-xs">
                      {title}
                    </span>
                  )}
                </CardTitle>
                <CardDescription className="flex items-center justify-between gap-2">
                  {progress.status === 'downloading' && 'Downloading via SSE stream...'}
                  {progress.status === 'complete' && 'Upload finished successfully.'}
                  {progress.status === 'error' && progress.message}
                  {progress.status === 'downloading' && (
                    <Button size="sm" variant="outline" onClick={handleCancelDownload}>
                      Cancel
                    </Button>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {progressPercent !== undefined ? (
                  <div className="flex items-center gap-3">
                    <Progress value={progressPercent} />
                    <Badge variant="secondary" className="text-xs">
                      {formatEta(progress.data?.etaSec)}
                    </Badge>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="bg-primary/20 relative h-2 w-full overflow-hidden rounded-full">
                      <div className="bg-primary h-full w-1/3 animate-pulse rounded-full" />
                    </div>
                    <Badge variant="secondary" className="text-xs">Calculating…</Badge>
                  </div>
                )}
                <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                  {progressPercent !== undefined && totalBytes !== undefined && totalBytes > 0 && (
                    <span>
                      <strong>{progressPercent}%</strong> of {formatBytes(totalBytes)}
                    </span>
                  )}
                  {downloadedBytes !== undefined && downloadedBytes >= 0 && (
                    <span>
                      {formatBytes(downloadedBytes)} downloaded
                    </span>
                  )}
                  {speedBps !== undefined && speedBps > 0 && (
                    <span>{formatSpeed(speedBps)}</span>
                  )}
                  {etaSec !== undefined && etaSec > 0 && (
                    <span className="hidden md:inline">{formatEta(etaSec)}</span>
                  )}
                  {downloadedBytes === undefined && speedBps === undefined && etaSec === undefined && (
                    <span>Waiting for download progress…</span>
                  )}
                </div>

                {/* Futuristic Console */}
                <div className="mt-4 rounded-lg border shadow-inner"
                     style={{
                       borderColor: '#6d28d9',
                       backgroundColor: '#0b1020',
                       boxShadow: '0 0 24px #6d28d944, inset 0 0 16px #0ea5e933'
                     }}>
                  <div className="flex items-center justify-between px-3 py-2"
                       style={{
                         background: 'linear-gradient(90deg, #111827 0%, #0f172a 100%)',
                         borderBottom: '1px solid #6d28d933'
                       }}>
                    <div className="flex items-center gap-3">
                      <span className="text-xs tracking-wider" style={{ color: '#a78bfa' }}>Console</span>
                      <div className="hidden md:flex items-center gap-1 text-[11px]">
                        <button
                          className={`px-2 py-0.5 rounded ${logView === 'summary' ? 'bg-[#6d28d933] text-[#a78bfa]' : 'text-[#93c5fd]'}`}
                          onClick={() => setLogView('summary')}
                        >Summary</button>
                        <button
                          className={`px-2 py-0.5 rounded ${logView === 'raw' ? 'bg-[#6d28d933] text-[#a78bfa]' : 'text-[#93c5fd]'}`}
                          onClick={() => setLogView('raw')}
                        >Raw</button>
                        <label className="ml-2 flex items-center gap-1 cursor-pointer select-none">
                          <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} />
                          <span className="text-[#93c5fd]">Auto-scroll</span>
                        </label>
                        <label className="ml-2 flex items-center gap-1 cursor-pointer select-none">
                          <input type="checkbox" checked={showTimestamps} onChange={(e) => setShowTimestamps(e.target.checked)} />
                          <span className="text-[#93c5fd]">Timestamps</span>
                        </label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => (logView === 'summary' ? setLogs([]) : setRawLogs([]))}>
                        Clear
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => navigator.clipboard.writeText((logView === 'summary' ? logs : rawLogs).map(l => l.line).join('\n'))}>
                        Copy
                      </Button>
                    </div>
                  </div>
                  <div ref={consoleRef} className="max-h-64 overflow-auto px-3 py-2 font-mono text-xs leading-5">
                    {(logView === 'summary' ? logs : rawLogs).length === 0 ? (
                      <div className="opacity-60" style={{ color: '#93c5fd' }}>Waiting for yt-dlp output…</div>
                    ) : (
                      (logView === 'summary' ? logs : rawLogs).map((entry, idx) => (
                        <div key={idx} className="whitespace-pre-wrap wrap-break-word">
                          <span className="mr-2 select-none" style={{ color: '#64748b' }}>▌</span>
                          <span style={{ color: entry.stream === 'stderr' ? '#f87171' : '#22d3ee', textShadow: '0 0 6px #22d3ee55' }}>
                            {showTimestamps ? `[${formatTime(entry.ts)}] ` : ''}{entry.line}
                          </span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {downloadResult && (
            <Card className="border-success/40 bg-success/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base text-success">
                  <ListChecks className="h-4 w-4" />
                  Download ready
                </CardTitle>
                <CardDescription>
                  Blob uploaded to Vercel – click below to open or copy the public URL.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{downloadResult.format}</Badge>
                  <Badge variant="secondary">{formatBytes(downloadResult.size)}</Badge>
                </div>
                <p className="break-all text-muted-foreground">{downloadResult.url}</p>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => window.open(downloadResult.url, '_blank', 'noopener,noreferrer')}>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Open URL
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => navigator.clipboard.writeText(downloadResult.url)}
                  >
                    Copy URL
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Preflight UI removed for a cleaner experience */}

          {info && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Info className="h-4 w-4" />
                  {info.title}
                </CardTitle>
                <CardDescription className="flex flex-wrap items-center gap-4 text-xs md:text-sm">
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {info.uploader ?? 'Unknown uploader'}
                  </span>
                  {info.duration !== undefined && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDuration(info.duration)}
                    </span>
                  )}
                  {info.viewCount !== undefined && (
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {formatViewCount(info.viewCount)} views
                    </span>
                  )}
                  {info.likeCount !== undefined && (
                    <span className="flex items-center gap-1">
                      <ThumbsUp className="h-3 w-3" />
                      {formatViewCount(info.likeCount)} likes
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {info.thumbnail && (
                  <div className="overflow-hidden rounded-lg border bg-muted/20">
                    <img src={info.thumbnail} alt="Thumbnail" className="h-auto w-full" />
                  </div>
                )}
                {info.bestFormat && (
                  <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                    <Badge variant="secondary">{info.bestFormat.format ?? info.bestFormat.formatId}</Badge>
                    {info.bestFormat.height && <Badge variant="outline">{info.bestFormat.height}p</Badge>}
                    <Badge variant="outline">
                      {formatBytes(info.bestFormat.filesize ?? info.bestFormat.filesizeApprox)}
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {availableFormats && availableFormats.length > 0 && (
            <Card className="border-[#1e3a8a33] bg-[#1e3a8a0d]">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg text-[#1e3a8a]">
                  <ListChecks className="h-4 w-4" />
                  Available Formats ({availableFormats.length})
                </CardTitle>
                <CardDescription>
                  All formats detected by yt-dlp for this video. The format selector will choose the best match.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="max-h-80 overflow-y-auto">
                  <div className="space-y-4">
                    {/* Video + Audio Combined Formats */}
                    {availableFormats.filter(f => f.vcodec && f.acodec && f.vcodec !== 'none' && f.acodec !== 'none').length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 text-[#1e3a8a]">Combined Video + Audio</h4>
                        <div className="grid gap-2">
                          {availableFormats
                            .filter(f => f.vcodec && f.acodec && f.vcodec !== 'none' && f.acodec !== 'none')
                            .slice(0, 8)
                            .map((format) => (
                              <FormatRow key={format.id} format={format} />
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Video Only Formats */}
                    {availableFormats.filter(f => f.vcodec && f.vcodec !== 'none' && (!f.acodec || f.acodec === 'none')).length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 text-[#1e3a8a]">Video Only</h4>
                        <div className="grid gap-2">
                          {availableFormats
                            .filter(f => f.vcodec && f.vcodec !== 'none' && (!f.acodec || f.acodec === 'none'))
                            .slice(0, 8)
                            .map((format) => (
                              <FormatRow key={format.id} format={format} />
                            ))}
                        </div>
                      </div>
                    )}

                    {/* Audio Only Formats */}
                    {availableFormats.filter(f => f.acodec && f.acodec !== 'none' && (!f.vcodec || f.vcodec === 'none')).length > 0 && (
                      <div>
                        <h4 className="text-sm font-medium mb-2 text-[#1e3a8a]">Audio Only</h4>
                        <div className="grid gap-2">
                          {availableFormats
                            .filter(f => f.acodec && f.acodec !== 'none' && (!f.vcodec || f.vcodec === 'none'))
                            .slice(0, 6)
                            .map((format) => (
                              <FormatRow key={format.id} format={format} />
                            ))}
                        </div>
                      </div>
                    )}

                    {availableFormats.length > 22 && (
                      <div className="text-center text-sm text-muted-foreground py-2 border-t border-[#1e3a8a22]">
                        ... and {availableFormats.length - 22} more formats available
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Reminder:</strong> Download only the content you have rights to. Respect YouTube&apos;s Terms of Service.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    </div>
  );
}

function FormatRow({ format }: { format: any }) {
  return (
    <div className="flex items-center justify-between rounded border border-[#1e3a8a33] p-2 text-sm hover:bg-[#6d28d914] transition-colors shadow-sm">
      <div className="flex items-center gap-2">
        <Badge variant="outline" className="font-mono text-xs border-[#6d28d9] text-[#6d28d9]">
          {format.id}
        </Badge>
        <span className="font-medium uppercase tracking-wide text-[#1e3a8a]">{format.ext}</span>
        {format.resolution && (
          <Badge variant="secondary" className="text-xs bg-[#1e3a8a14] text-[#1e3a8a]">
            {format.resolution}
          </Badge>
        )}
        {format.fps && (
          <Badge variant="outline" className="text-xs border-[#1e3a8a] text-[#1e3a8a]">
            {format.fps}fps
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {(format.filesize || format.filesize_approx) && (
          <span>{formatBytes(format.filesize || format.filesize_approx)}</span>
        )}
        {format.tbr && <span>{format.tbr}k</span>}
        {format.vcodec && format.acodec && format.vcodec !== 'none' && format.acodec !== 'none' && (
          <span className="font-mono text-xs">{format.vcodec}/{format.acodec}</span>
        )}
        {format.vcodec && format.vcodec !== 'none' && (!format.acodec || format.acodec === 'none') && (
          <span className="font-mono text-xs">{format.vcodec}</span>
        )}
        {format.acodec && format.acodec !== 'none' && (!format.vcodec || format.vcodec === 'none') && (
          <span className="font-mono text-xs">{format.acodec}</span>
        )}
        {format.format_note && <span>{format.format_note}</span>}
      </div>
    </div>
  );
}

function parseVideoInfo(raw: any): ParsedVideoInfo {
  const formats: any[] = Array.isArray(raw?.formats) ? raw.formats : [];
  const best = formats.find((item) => item.vcodec !== 'none' && item.acodec !== 'none') ?? formats[0];
  return {
    title: raw?.title ?? 'Untitled video',
    duration: typeof raw?.duration === 'number' ? raw.duration : undefined,
    thumbnail: raw?.thumbnail,
    uploader: raw?.uploader,
    viewCount: typeof raw?.view_count === 'number' ? raw.view_count : undefined,
    likeCount: typeof raw?.like_count === 'number' ? raw.like_count : undefined,
    bestFormat: best
      ? {
          formatId: best.format_id,
          format: best.format,
          ext: best.ext,
          height: best.height,
          filesize: best.filesize,
          filesizeApprox: best.filesize_approx,
          formatNote: best.format_note,
        }
      : undefined,
    raw,
  };
}

function formatViewCount(value?: number) {
  if (value === undefined) return '—';
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`;
  return value.toLocaleString();
}

function formatDuration(seconds?: number) {
  if (!seconds || seconds <= 0) return '—';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function formatBytes(value?: number | string | null) {
  if (value === undefined || value === null) return 'unknown size';
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(numeric) || numeric <= 0) return 'unknown size';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let idx = 0;
  let num = numeric;
  while (num >= 1024 && idx < units.length - 1) {
    num /= 1024;
    idx += 1;
  }
  return `${num.toFixed(num >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
}

function formatSpeed(value?: number) {
  if (!value || value <= 0) return '—';
  return `${formatBytes(value)}/s`;
}

function formatEta(value?: number) {
  if (!value || value <= 0) return '≈ ready';
  const minutes = Math.floor(value / 60);
  const seconds = Math.floor(value % 60);
  if (minutes > 0) {
    return `${minutes}m ${seconds}s remaining`;
  }
  return `${seconds}s remaining`;
}
